/**
 * AllocationMatrixSection — the numbers, the whole numbers, only the numbers.
 *
 * Two side-by-side matrices: by content type and by channel. This is
 * where a marketing director confirms the plan actually adds up.
 */
import { Card } from "@/shared/ui/card";
import { CHANNEL_LABEL, CONTENT_TYPE_LABEL } from "../../types";
import type { AllocationBlock } from "../../types";

export function AllocationMatrixSection({
  allocation,
}: {
  allocation: AllocationBlock;
}): JSX.Element {
  const contentRows = Object.entries(allocation.by_content_type).sort(
    ([a], [b]) => (CONTENT_TYPE_LABEL[a] ?? a).localeCompare(CONTENT_TYPE_LABEL[b] ?? b),
  );
  const channelRows = Object.entries(allocation.by_channel).sort(
    ([a], [b]) => (CHANNEL_LABEL[a] ?? a).localeCompare(CHANNEL_LABEL[b] ?? b),
  );

  return (
    <section data-testid="strategy-allocation-matrix">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Allocation Matrix
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          {allocation.total_assets_planned} assets · {allocation.weeks} weeks
        </h2>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="allocation-by-content-type">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
            By content type
          </p>
          <ul className="divide-y divide-border">
            {contentRows.map(([key, qty]) => (
              <li
                key={key}
                className="flex items-center justify-between py-2.5 text-sm"
              >
                <span>{CONTENT_TYPE_LABEL[key] ?? key}</span>
                <span className="font-semibold tabular-nums">{qty}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-6" data-testid="allocation-by-channel">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
            By channel
          </p>
          <ul className="divide-y divide-border">
            {channelRows.map(([key, qty]) => (
              <li
                key={key}
                className="flex items-center justify-between py-2.5 text-sm"
              >
                <span>{CHANNEL_LABEL[key] ?? key}</span>
                <span className="font-semibold tabular-nums">{qty}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </section>
  );
}
